import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Compass, RefreshCw, Plus, Tag, X, CheckSquare, Square, Info, ListPlus, Library, Loader2, BookOpen, Heart, Share2 } from "lucide-react";
import { toast } from "sonner";
import { getStoredLang } from "../components/LanguagePicker";
import { t, useLang } from "../lib/i18n";

// Module-scoped cache — survives navigation away and back, but not a hard
// browser refresh. Keyed by sectionId (empty string = "all recommendations").
// Storing it here (rather than in Context) keeps the wiring tiny and avoids
// re-rendering the whole app when the cache changes.
const _recsCache = new Map(); // sectionId -> { recs, message, ts }

export default function RecommendationsPage() {
  useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionId = searchParams.get("section") || "";
  const navigate = useNavigate();

  const cached = _recsCache.get(sectionId);
  const [recs, setRecs] = useState(cached?.recs || []);
  const [message, setMessage] = useState(cached?.message || "");
  // If we already have a cached list, skip the loading state entirely.
  const [loading, setLoading] = useState(!cached);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);

  // Multi-select state — indexes (matching the `recs` array) so duplicates by
  // title don't collide.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showBulkList, setShowBulkList] = useState(false);
  const [showBulkSeries, setShowBulkSeries] = useState(false);

  // "View details" modal — populated with a rec object on click.
  const [details, setDetails] = useState(null);

  useEffect(() => {
    api.get("/sections").then(({ data }) => setSections(data || [])).catch(() => setSections([]));
  }, []);

  useEffect(() => {
    if (!sectionId) { setActiveSection(null); return; }
    const found = sections.find((s) => s.id === sectionId);
    setActiveSection(found || null);
  }, [sectionId, sections]);

  const fetchPage = async (pageNum, exclude) => {
    if (sectionId) {
      const { data } = await api.get(`/sections/${sectionId}/recommendations`);
      return data;
    }
    const params = { page: pageNum };
    if (exclude && exclude.length) params.exclude = exclude.join("|");
    const { data } = await api.get("/recommendations", { params });
    return data;
  };

  const initialLoad = async ({ force = false } = {}) => {
    // Skip re-fetching if we've already got a cached set for this section
    // and the caller didn't press the "Regenerate" button.
    if (!force && _recsCache.has(sectionId)) {
      const c = _recsCache.get(sectionId);
      setRecs(c.recs);
      setMessage(c.message || "");
      setLoading(false);
      return;
    }
    setLoading(true); setError(""); setSelected(new Set());
    try {
      const data = await fetchPage(0, []);
      const nextRecs = data.recommendations || [];
      const nextMsg = data.message || "";
      setRecs(nextRecs);
      setMessage(nextMsg);
      _recsCache.set(sectionId, { recs: nextRecs, message: nextMsg, ts: Date.now() });
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const exclude = recs.map((r) => `${r.title} by ${r.author}`);
      const data = await fetchPage(recs.length, exclude);
      const next = data.recommendations || [];
      if (next.length === 0) toast.message(t("recs.no_more_fresh"));
      const seen = new Set(exclude.map((s) => s.toLowerCase()));
      const fresh = next.filter((r) => !seen.has(`${r.title} by ${r.author}`.toLowerCase()));
      if (fresh.length === 0) toast.message(t("recs.all_we_have"));
      setRecs((prev) => {
        const merged = [...prev, ...fresh];
        _recsCache.set(sectionId, { recs: merged, message, ts: Date.now() });
        return merged;
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { initialLoad(); /* eslint-disable-next-line */ }, [sectionId]);

  const clearSection = () => setSearchParams({}, { replace: true });
  const selectSection = (id) => setSearchParams({ section: id }, { replace: true });

  const toggleSelect = (i) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  // Resolve a rec via Open Library — same 4-strategy fallback as single add.
  const resolveOnOpenLibrary = async (r) => {
    const candidates = [
      { title: r.title, author: r.author },
      { q: `${r.title} ${r.author}` },
      { title: r.title.replace(/^[^:\u2014-]*[:\u2014-]\s*/, ""), author: r.author },
      { title: r.title.replace(/^[^:\u2014-]*[:\u2014-]\s*/, "") },
    ];
    const fields = "key,title,author_name,first_publish_year,cover_i,number_of_pages_median,subject";
    for (const c of candidates) {
      const params = new URLSearchParams({ ...c, limit: "3", fields });
      try {
        const res = await fetch(`https://openlibrary.org/search.json?${params}`);
        const data = await res.json();
        const doc = data?.docs?.[0];
        if (doc && doc.key) return doc;
      } catch { /* try next */ }
    }
    return null;
  };

  // Add a single rec to the user's library; returns the created book or null.
  const addOneToLibrary = async (r) => {
    const doc = await resolveOnOpenLibrary(r);
    if (!doc) return { error: `Couldn't find "${r.title}" on Open Library` };
    const payload = {
      title: doc.title || r.title,
      author: (doc.author_name && doc.author_name[0]) || r.author,
      cover_url: doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg` : "",
      genre: r.genre,
      age_rating: r.age_rating,
      year: doc.first_publish_year || null,
      pages: doc.number_of_pages_median || null,
      description: r.reason,
      openlibrary_key: doc.key,
    };
    try {
      const { data } = await api.post("/books", payload);
      return { book: data };
    } catch (e) {
      return { error: formatApiError(e) };
    }
  };

  // Bulk pipeline shared by all three bulk actions.
  const bulkAdd = async ({ sectionId: secId, seriesName, startIndex } = {}) => {
    const picks = Array.from(selected).map((i) => recs[i]).filter(Boolean);
    if (!picks.length) return;
    setBulkBusy(true);
    try {
      const created = [];
      const failures = [];
      for (const r of picks) {
        const res = await addOneToLibrary(r);
        if (res.book) created.push(res.book);
        else failures.push({ rec: r, error: res.error });
      }
      const bookIds = created.map((b) => b.id);
      // Attach to a list if requested.
      if (secId && bookIds.length) {
        await Promise.allSettled(bookIds.map((id) => api.post(`/sections/${secId}/books`, { book_id: id })));
      }
      // Tag as a series if requested.
      if (seriesName && bookIds.length) {
        const body = { book_ids: bookIds, series_name: seriesName };
        if (startIndex != null && !Number.isNaN(startIndex)) body.start_index = startIndex;
        await api.post("/books/batch-set-series", body);
      }
      let msg = `${t("recs.added_to_library")} — ${created.length}`;
      if (seriesName) msg += ` · "${seriesName}"`;
      toast.success(msg);
      if (failures.length) toast.message(`${failures.length} ${t("recs.cant_add")}`);
      clearSelection();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="recommendations-page">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#0A0A0A] mb-3 inline-flex items-center gap-2">
            <Compass className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("recs.eyebrow")}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] tracking-tight">
            {activeSection ? <>{t("recs.picks_for")} <em>{activeSection.name}</em></> : t("recs.title")}
          </h1>
          <p className="text-[#4A4030] mt-3 max-w-xl">
            {activeSection ? t("recs.subtitle_section") : t("recs.subtitle_all")}
          </p>
        </div>
        <button onClick={() => initialLoad({ force: true })} disabled={loading}
          className="inline-flex items-center gap-2 border border-[rgba(0,0,0,0.18)] text-[#0A0A0A] font-mono uppercase tracking-widest text-xs px-5 py-3 rounded-sm hover:bg-black/5 disabled:opacity-60 transition-colors"
          data-testid="refresh-recs">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> {t("recs.regenerate")}
        </button>
      </div>

      {/* Section filter chips */}
      {sections.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="recs-section-filter">
          <span className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mr-2 inline-flex items-center gap-1.5"><Tag className="w-3 h-3" strokeWidth={1.5} /> {t("recs.filter_by_list")}</span>
          <button
            onClick={clearSection}
            className={`font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm border transition-colors ${
              !sectionId ? "bg-[#0A0A0A] text-[#F2EAD3] border-[#0A0A0A]" : "bg-[#FAF5E6] text-[#0A0A0A] border-[rgba(10,10,10,0.18)] hover:bg-black/5"
            }`}
            data-testid="recs-section-all"
          >{t("recs.all_favourites")}</button>
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => selectSection(s.id)}
              className={`font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm border transition-colors ${
                sectionId === s.id ? "bg-[#0A0A0A] text-[#F2EAD3] border-[#0A0A0A]" : "bg-[#FAF5E6] text-[#0A0A0A] border-[rgba(10,10,10,0.18)] hover:bg-black/5"
              }`}
              data-testid={`recs-section-${s.id}`}
            >{s.name}</button>
          ))}
          {sectionId && (
            <button onClick={clearSection} className="inline-flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A] px-2" data-testid="recs-section-clear">
              <X className="w-3 h-3" strokeWidth={1.5} /> {t("recs.clear")}
            </button>
          )}
        </div>
      )}

      {/* Bulk selection toolbar */}
      {recs.length > 0 && (
        <div className="sticky top-16 z-30 -mx-6 md:-mx-12 px-6 md:px-12 py-3 mb-6 bg-[#F2EAD3]/95 backdrop-blur-sm border-y border-[rgba(0,0,0,0.08)] flex items-center justify-between gap-3 flex-wrap" data-testid="recs-toolbar">
          <span className="font-mono uppercase tracking-widest text-[10px] text-[#525252]" data-testid="recs-selected-count">
            {selected.size} {t("recs.of_word")} {recs.length} {t("recs.selected")}
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => bulkAdd()}
              disabled={!selected.size || bulkBusy}
              className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
              data-testid="bulk-add-to-library"
            >
              {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} /> : <BookOpen className="w-3 h-3" strokeWidth={1.5} />} {t("recs.add_to_lib_btn")}
            </button>
            <button
              onClick={() => setShowBulkList(true)}
              disabled={!selected.size || bulkBusy}
              className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
              data-testid="bulk-add-to-list"
            >
              <ListPlus className="w-3 h-3" strokeWidth={1.5} /> {t("recs.add_to_list_btn")}
            </button>
            <button
              onClick={() => setShowBulkSeries(true)}
              disabled={!selected.size || bulkBusy}
              className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
              data-testid="bulk-add-to-series"
            >
              <Library className="w-3 h-3" strokeWidth={1.5} /> {t("recs.add_to_series_btn")}
            </button>
            <button
              onClick={async () => {
                if (!selected.size || bulkBusy) return;
                setBulkBusy(true);
                let ok = 0, fail = 0;
                for (const i of selected) {
                  const r = recs[i];
                  const res = await addOneToLibrary(r);
                  if (!res.book) { fail++; continue; }
                  try { await api.post("/me/wishlist", { book_id: res.book.id }); ok++; }
                  catch { fail++; }
                }
                setBulkBusy(false);
                clearSelection();
                if (ok) toast.success(`Added ${ok} to wishlist`);
                if (fail) toast.error(`${fail} couldn't be added`);
              }}
              disabled={!selected.size || bulkBusy}
              className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
              data-testid="bulk-add-to-wishlist"
            >
              <Heart className="w-3 h-3" strokeWidth={1.5} /> Add to wishlist
            </button>
            <button
              onClick={async () => {
                if (!selected.size) return;
                const picks = Array.from(selected).map((i) => recs[i]).filter(Boolean);
                const list = picks.map((r) => `• ${r.title} — ${r.author}`).join("\n");
                const shareText = `Book picks from Reads:\n\n${list}\n\nBrowse and log your own reads at https://readsco.org`;
                try {
                  if (navigator.share) {
                    await navigator.share({ title: "Reading picks from Reads", text: shareText });
                  } else {
                    await navigator.clipboard.writeText(shareText);
                    toast.success("Copied to clipboard — paste it to your friend");
                  }
                } catch (e) {
                  if (e.name !== "AbortError") toast.error("Couldn't share");
                }
              }}
              disabled={!selected.size}
              className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
              data-testid="bulk-share"
            >
              <Share2 className="w-3 h-3" strokeWidth={1.5} /> Share with a friend
            </button>
            {selected.size > 0 && (
              <button onClick={clearSelection} className="p-1.5 hover:bg-black/5 rounded-sm" aria-label="Clear selection" data-testid="recs-clear-selection">
                <X className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="font-mono uppercase tracking-widest text-xs text-[#A3A3A3]" data-testid="recs-loading">{t("recs.reading_shelf")}</div>
      ) : error ? (
        <div className="border border-[#B91C1C]/20 bg-[#B91C1C]/5 text-[#B91C1C] text-sm p-4 rounded-sm" data-testid="recs-error">{error}</div>
      ) : message ? (
        <div className="border border-dashed border-[rgba(0,0,0,0.12)] p-16 text-center">
          <div className="font-heading text-2xl text-[#525252]" data-testid="recs-empty">{message}</div>
          <Link to={activeSection ? "/lists" : "/discover"} className="font-mono uppercase tracking-widest text-xs text-[#0A0A0A] hover:underline mt-4 inline-block">
            {activeSection ? t("recs.back_to_lists") : t("recs.browse_library")}
          </Link>
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-px bg-[rgba(0,0,0,0.08)] border border-[rgba(0,0,0,0.08)]" data-testid="recs-grid">
            {recs.map((r, i) => {
              const isPicked = selected.has(i);
              return (
                <article key={`${r.title}-${i}`} className={`bg-[#FAF5E6] p-8 md:p-10 transition-colors ${isPicked ? "ring-2 ring-inset ring-[#0A0A0A]" : ""}`} data-testid={`rec-${i}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] flex items-center gap-3">
                      <span>{r.genre}</span>
                      <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
                      <span>{r.age_rating}</span>
                    </div>
                    <button
                      onClick={() => toggleSelect(i)}
                      className="p-1 -mr-1 hover:bg-black/5 rounded-sm"
                      aria-label={isPicked ? "Deselect" : "Select"}
                      data-testid={`rec-select-${i}`}
                    >
                      {isPicked
                        ? <CheckSquare className="w-4 h-4 text-[#0A0A0A]" strokeWidth={1.5} />
                        : <Square className="w-4 h-4 text-[#8C7F65]" strokeWidth={1.5} />}
                    </button>
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl text-[#0A0A0A] mt-3 leading-tight tracking-tight">{r.title}</h2>
                  <div className="font-heading text-lg text-[#525252] mt-1">{t("recs.by")} {r.author}</div>
                  <p className="text-[#0A0A0A] mt-5 leading-relaxed text-[15px]">{r.reason}</p>
                  <div className="mt-6 pt-5 border-t border-[rgba(0,0,0,0.08)] flex items-center justify-between gap-3 flex-wrap">
                    <span className="font-mono uppercase tracking-widest text-[10px] text-[#A3A3A3]">#{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setDetails(r)}
                        className="inline-flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A]"
                        data-testid={`rec-details-${i}`}
                      >
                        <Info className="w-3 h-3" strokeWidth={1.5} /> {t("recs.view_details")}
                      </button>
                      <button
                        onClick={async () => {
                          const res = await addOneToLibrary(r);
                          if (res.book) {
                            toast.success(t("recs.added_to_library"));
                            // Use react-router navigate so the recs page stays
                            // mounted in the history stack — the user hitting
                            // "back" lands on the same cached recommendations
                            // instead of triggering a fresh LLM call.
                            navigate(`/books/${res.book.id}`);
                          }
                          else toast.error(res.error || t("recs.cant_add"));
                        }}
                        className="font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] hover:underline"
                        data-testid={`add-rec-${i}`}
                      >{t("recs.add_to_library")} →</button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {!sectionId && (
            <div className="flex flex-col items-center gap-3 mt-12">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#262626] disabled:opacity-50 text-white font-mono uppercase tracking-widest text-xs px-8 py-4 rounded-sm transition-colors"
                data-testid="load-more-recs"
              >
                {loadingMore ? (
                  <><RefreshCw className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> {t("recs.generating")}</>
                ) : (
                  <><Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("recs.load_more")}</>
                )}
              </button>
              <span className="font-mono uppercase tracking-widest text-[10px] text-[#A3A3A3]" data-testid="recs-count">
                {recs.length} {t("recs.picks_so_far")}
              </span>
            </div>
          )}
        </>
      )}

      <RecDetailsModal
        rec={details}
        onClose={() => setDetails(null)}
        onAddToLibrary={async (r, chosenRating) => {
          const res = await addOneToLibrary(r);
          if (res.book) {
            // If the reader picked a rating in the inline picker, record it
            // before we navigate them to the full detail page — that page
            // is authoritative for the review text, but the star is set now.
            if (chosenRating && chosenRating > 0) {
              try { await api.post(`/books/${res.book.id}/reviews`, { rating: chosenRating, text: "" }); }
              catch { /* non-fatal — user can rate again on the detail page */ }
            }
            setDetails(null);
            toast.success(t("recs.added_to_library"));
            navigate(`/books/${res.book.id}`);
          } else {
            toast.error(res.error || t("recs.cant_add"));
          }
        }}
        onAddToWishlist={async (r) => {
          const res = await addOneToLibrary(r);
          if (!res.book) { toast.error(res.error || t("recs.cant_add")); return; }
          try {
            await api.post("/me/wishlist", { book_id: res.book.id });
            toast.success("Added to wishlist");
          } catch (e) { toast.error(formatApiError(e)); }
        }}
      />
      <BulkListModal
        open={showBulkList}
        sections={sections}
        count={selected.size}
        onClose={() => setShowBulkList(false)}
        onPick={async (secId) => { setShowBulkList(false); await bulkAdd({ sectionId: secId }); }}
        onCreate={async (name) => {
          try {
            const { data } = await api.post("/sections", { name });
            setSections((prev) => [...prev, data]);
            setShowBulkList(false);
            await bulkAdd({ sectionId: data.id });
          } catch (e) { toast.error(formatApiError(e)); }
        }}
      />
      <BulkSeriesModal
        open={showBulkSeries}
        count={selected.size}
        onClose={() => setShowBulkSeries(false)}
        onSubmit={async (name, startIndex) => { setShowBulkSeries(false); await bulkAdd({ seriesName: name, startIndex }); }}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// "View details" — lazy-loads richer info from Open Library so the user can
// preview a recommendation without adding it.
// -----------------------------------------------------------------------------
function RecDetailsModal({ rec, onClose, onAddToLibrary, onAddToWishlist }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  // Inline rate-on-add flow — user clicks "Add to library" once to reveal
  // the star picker, then clicks "Save with rating" to commit.
  const [ratePickerOpen, setRatePickerOpen] = useState(false);
  const [pickedRating, setPickedRating] = useState(0);

  useEffect(() => {
    if (!rec) { setInfo(null); return; }
    setLoading(true);
    const ctrl = new AbortController();
    (async () => {
      const lang = getStoredLang();
      const fields = "key,title,author_name,first_publish_year,cover_i,number_of_pages_median,subject,language,ratings_average,ratings_count";
      const candidates = [
        { title: rec.title, author: rec.author },
        { q: `${rec.title} ${rec.author}` },
        { title: rec.title.replace(/^[^:\u2014-]*[:\u2014-]\s*/, ""), author: rec.author },
      ];
      let doc = null;
      for (const c of candidates) {
        const params = new URLSearchParams({ ...c, limit: "3", fields, language: lang });
        try {
          const r = await fetch(`https://openlibrary.org/search.json?${params}`, { signal: ctrl.signal });
          const data = await r.json();
          doc = data?.docs?.[0];
          if (doc?.key) break;
        } catch { /* try next */ }
      }
      let description = "";
      if (doc?.key) {
        try {
          const w = await fetch(`https://openlibrary.org${doc.key}.json`, { signal: ctrl.signal });
          const work = await w.json();
          if (typeof work.description === "string") description = work.description;
          else if (work.description?.value) description = work.description.value;
          description = description.split("---")[0].trim();
        } catch { /* description is optional */ }
      }
      setInfo({ doc, description });
      setLoading(false);
    })();
    return () => ctrl.abort();
  }, [rec]);

  if (!rec) return null;
  const doc = info?.doc;
  const cover = doc?.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg` : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm" onClick={onClose} data-testid="rec-details-modal">
      <div className="bg-[#F2EAD3] max-w-2xl w-full max-h-[85vh] flex flex-col border border-[rgba(10,10,10,0.10)] shadow-[0_20px_60px_rgba(10,10,10,0.20)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-[rgba(10,10,10,0.08)] flex-shrink-0">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-2">{t("recs.book_details")}</div>
            <h3 className="font-heading text-2xl text-[#0A0A0A] tracking-tight leading-tight">{rec.title}</h3>
            <div className="font-heading text-base text-[#525252] mt-1">{t("recs.by")} {rec.author}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-sm" aria-label="Close" data-testid="rec-details-close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {loading && <div className="font-mono uppercase tracking-widest text-xs text-[#525252] text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-3" strokeWidth={1.5} /> {t("recs.looking_up")}</div>}
          {!loading && info && (
            <div className="flex gap-6 flex-col sm:flex-row">
              <div className="w-32 sm:w-40 flex-shrink-0 bg-[#E8DFC1] border border-[rgba(10,10,10,0.10)] aspect-[2/3] overflow-hidden">
                {cover && <img src={cover} alt={rec.title} className="w-full h-full object-cover" loading="lazy" />}
              </div>
              <div className="flex-1 min-w-0 space-y-4 text-[15px] leading-relaxed">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <Stat label={t("recs.year")} value={doc?.first_publish_year || "—"} />
                  <Stat label={t("recs.pages")} value={doc?.number_of_pages_median || "—"} />
                  <Stat label={t("addbook.genre")} value={rec.genre} />
                  <Stat label={t("discover.age_label")} value={rec.age_rating} />
                  {doc?.ratings_average && <Stat label={t("recs.rating")} value={`${doc.ratings_average.toFixed(2)} / 5`} />}
                  {doc?.ratings_count && <Stat label={t("recs.reviews")} value={doc.ratings_count} />}
                </div>
                <div>
                  <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">{t("recs.why_picked")}</div>
                  <p className="text-[#0A0A0A]">{rec.reason}</p>
                </div>
                {info.description && (
                  <div>
                    <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">{t("recs.ol_summary")}</div>
                    <p className="text-[#0A0A0A] whitespace-pre-line">{info.description.slice(0, 1200)}</p>
                  </div>
                )}
                {!doc && (
                  <p className="text-sm text-[#525252] italic">{t("recs.no_extra_info")}</p>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Action bar — "Already read? Add to library" + wishlist + share. */}
        <div className="flex-shrink-0 border-t border-[rgba(10,10,10,0.10)] bg-[#FAF5E6] p-5 md:p-6 flex flex-wrap items-center justify-between gap-3">
          <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252]" data-testid="rec-details-cta-label">
            Already read?
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={async () => {
                if (!onAddToWishlist) return;
                setAdding(true);
                await onAddToWishlist(rec);
                setAdding(false);
              }}
              disabled={adding}
              className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#F2EAD3] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-2 rounded-sm transition-colors disabled:opacity-40"
              data-testid="rec-details-add-wishlist"
            >
              <Heart className="w-3 h-3" strokeWidth={1.5} /> Add to wishlist
            </button>
            <button
              type="button"
              onClick={async () => {
                const text = `${rec.title} — ${rec.author}\n\nFound on Reads: ${rec.reason}\n\nhttps://readsco.org`;
                try {
                  if (navigator.share) await navigator.share({ title: rec.title, text });
                  else { await navigator.clipboard.writeText(text); toast.success("Copied — paste it to your friend"); }
                } catch (e) { if (e.name !== "AbortError") toast.error("Couldn't share"); }
              }}
              className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#F2EAD3] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-2 rounded-sm transition-colors"
              data-testid="rec-details-share"
            >
              <Share2 className="w-3 h-3" strokeWidth={1.5} /> Share
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!onAddToLibrary) return;
                if (!ratePickerOpen) {
                  // First tap → reveal the star picker. Second tap (from
                  // the picker's confirm button) commits with the rating.
                  setRatePickerOpen(true);
                  return;
                }
                setAdding(true);
                await onAddToLibrary(rec, pickedRating);
                setAdding(false);
              }}
              disabled={adding}
              className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] px-3.5 py-2 rounded-sm transition-colors disabled:opacity-50"
              data-testid="rec-details-add-library"
            >
              {adding ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} /> : <BookOpen className="w-3 h-3" strokeWidth={1.5} />}
              {ratePickerOpen ? (pickedRating ? `Save · ${pickedRating}★` : "Save without rating") : "Add to library"}
            </button>
          </div>
        </div>
        {ratePickerOpen && (
          <div className="flex-shrink-0 border-t border-[rgba(10,10,10,0.10)] bg-[#F2EAD3] p-5 md:p-6" data-testid="rec-details-rate-picker">
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#4A4030] mb-3">Rate as you add · optional</div>
            <div className="flex items-center gap-2">
              {[1,2,3,4,5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPickedRating(n === pickedRating ? 0 : n)}
                  className="p-1 transition-transform hover:scale-110"
                  aria-label={`${n} stars`}
                  data-testid={`rec-rate-star-${n}`}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill={n <= pickedRating ? "#8B7100" : "none"} stroke={n <= pickedRating ? "#8B7100" : "#0A0A0A"} strokeWidth="1.8">
                    <polygon points="12,2 15,9 22,10 17,15 18,22 12,18 6,22 7,15 2,10 9,9" />
                  </svg>
                </button>
              ))}
              {pickedRating > 0 && (
                <button
                  type="button"
                  onClick={() => setPickedRating(0)}
                  className="ml-2 font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A]"
                  data-testid="rec-rate-clear"
                >
                  clear
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#8C7F65]">{label}</div>
      <div className="text-[#0A0A0A] mt-0.5">{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pick-an-existing-or-create-a-list dialog (for bulk add).
// -----------------------------------------------------------------------------
function BulkListModal({ open, sections, count, onClose, onPick, onCreate }) {
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (open) { setNewName(""); setCreating(false); } }, [open]);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={onClose} data-testid="bulk-list-modal">
      <div className="bg-[#F2EAD3] max-w-md w-full max-h-[80vh] overflow-y-auto border border-[rgba(10,10,10,0.10)] shadow-[0_20px_60px_rgba(10,10,10,0.20)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-[rgba(10,10,10,0.08)]">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-2 inline-flex items-center gap-2"><ListPlus className="w-3 h-3" strokeWidth={1.5} /> {t("recs.add_to_list_btn")}</div>
            <h3 className="font-heading text-2xl text-[#0A0A0A] tracking-tight">{count} {count === 1 ? t("recs.pick_singular") : t("recs.picks_plural")} {t("recs.selected")}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-sm" aria-label="Close" data-testid="bulk-list-close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-[#525252] leading-relaxed">{t("recs.bulk_list_body")}</p>
          {sections.length > 0 ? (
            <ul className="space-y-2" data-testid="bulk-list-options">
              {sections.map((s) => (
                <li key={s.id}>
                  <button onClick={() => onPick(s.id)} className="w-full text-left px-3 py-2.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 transition-colors" data-testid={`bulk-list-${s.id}`}>
                    <div className="text-[#0A0A0A]">{s.name}</div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-[#8C7F65]">{t("recs.no_lists_yet")}</div>
          )}
          <div className="pt-5 border-t border-[rgba(10,10,10,0.08)]">
            <label htmlFor="bulk-new-list" className="block font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">{t("recs.or_create")}</label>
            <div className="flex gap-2">
              <input
                id="bulk-new-list"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t("lists.example_placeholder")}
                className="flex-1 bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] px-3 py-2 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
                data-testid="bulk-list-new-name"
              />
              <button
                onClick={async () => { setCreating(true); await onCreate(newName.trim()); setCreating(false); }}
                disabled={creating || !newName.trim()}
                className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-4 rounded-sm transition-colors disabled:opacity-50"
                data-testid="bulk-list-create"
              >{t("recs.create")}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Add picks to a (new or existing) series — same shape as MyLibrary's modal.
// -----------------------------------------------------------------------------
function BulkSeriesModal({ open, count, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [startIndex, setStartIndex] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(""); setStartIndex(""); setSaving(false); } }, [open]);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={onClose} data-testid="bulk-series-modal">
      <div className="bg-[#F2EAD3] max-w-md w-full max-h-[80vh] overflow-y-auto border border-[rgba(10,10,10,0.10)] shadow-[0_20px_60px_rgba(10,10,10,0.20)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-[rgba(10,10,10,0.08)]">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-2 inline-flex items-center gap-2"><Library className="w-3 h-3" strokeWidth={1.5} /> {t("recs.add_to_series_btn")}</div>
            <h3 className="font-heading text-2xl text-[#0A0A0A] tracking-tight">{count} {count === 1 ? t("recs.pick_singular") : t("recs.picks_plural")} {t("recs.selected")}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-sm" aria-label="Close" data-testid="bulk-series-close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-[#525252] leading-relaxed">{t("recs.bulk_series_body")}</p>
          <div>
            <label htmlFor="bulk-series-name" className="block font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">{t("recs.series_name")}</label>
            <input
              id="bulk-series-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Mistborn"
              autoFocus
              className="w-full bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] px-3 py-2.5 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
              data-testid="bulk-series-name"
            />
          </div>
          <div>
            <label htmlFor="bulk-series-start" className="block font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">{t("recs.starting_number")} <span className="normal-case tracking-normal text-[#8C7F65]">({t("recs.optional_word")})</span></label>
            <input
              id="bulk-series-start"
              type="number" step="any"
              value={startIndex}
              onChange={(e) => setStartIndex(e.target.value)}
              placeholder="1"
              className="w-32 bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] px-3 py-2.5 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
              data-testid="bulk-series-start"
            />
          </div>
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-[rgba(10,10,10,0.08)]">
            <button onClick={onClose} className="font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A] px-3 py-2" data-testid="bulk-series-cancel">{t("recs.cancel")}</button>
            <button
              onClick={async () => {
                if (!name.trim()) return;
                setSaving(true);
                const parsed = startIndex.trim() === "" ? null : Number(startIndex);
                await onSubmit(name.trim(), parsed);
                setSaving(false);
              }}
              disabled={saving || !name.trim()}
              className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors disabled:opacity-50"
              data-testid="bulk-series-submit"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <Library className="w-3.5 h-3.5" strokeWidth={1.5} />} {t("recs.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
